import React from "react";
import { colors } from '../../../../theme/colors';

import {
  Avatar,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Tooltip,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BlockIcon from "@mui/icons-material/Block";
import { MainWrapper } from "./style";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Zoom from "@mui/material/Zoom";
import { CustomTextField } from "../../../../components/common-components/CustomTextField";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomConfirmModel from "../../../../components/common-components/CustomConfirmModel";
import useScheduleInterviewList from "./useScheduleInterviewList";

const ScheduledInterviewList = () => {
  const {
    filterName,
    open,
    candiName,
    filterOptions,
    cancelLoading,
    interview,
    headCells,
    dateFilter,
    customDate,
    handleClose,
    handleClickOpen,
    handleFilterChange,
    handleStatusChange,
    handleInterviewTypeChange,
    handleDateFilterChange,
    handleCustomDateChange,
    handleRowsPerPageChange,
    handlePrevPage,
    handleNextPage,
    handleEditCandidate,
    handleCancel,
    handleFormateDate,
    handleExportInterviewReport,
    downloading,
    handleResetFilters,
  } = useScheduleInterviewList();

  const activeFilterCount =
    (filterName ? 1 : 0) +
    (filterOptions.status !== "All" ? 1 : 0) +
    (filterOptions.interviewType !== "All" ? 1 : 0) +
    (dateFilter !== "All" ? 1 : 0);

  const SkeletonRow = () => (
    <TableRow>
      <TableCell>
        <Box className="table_user-primary-detail">
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ ml: 2 }}>
            <Skeleton variant="text" width={120} height={30} />
            <Skeleton variant="text" width={150} height={20} />
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box className="table_user-primary-detail">
          <Box>
            <Skeleton variant="text" width={100} height={30} />
            <Skeleton variant="text" width={140} height={16} />
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={120} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={100} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={100} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={180} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={180} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={150} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="rounded" width={90} height={28} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={80} height={30} />
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.4rem",
          }}
        >
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <MainWrapper>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color={colors.primary}>
          Scheduled Interviews
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View, manage, and track all scheduled AI interviews.
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 3,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 20px rgba(15, 23, 42, 0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            pb: 2,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { md: 450 } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by recruiter or candidate name..."
              value={filterName}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
                endAdornment: filterName ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleFilterChange({ target: { value: "" } })}
                      edge="end"
                      sx={{ color: "#94a3b8" }}
                    >
                      <ClearIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8fafc",
                  borderRadius: "12px",
                  fontSize: "0.875rem",
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: colors.primaryLightBorder },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                    "& fieldset": { borderColor: colors.primary },
                    boxShadow: `0 0 0 3px ${colors.primaryLight}`,
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f8fafc",
              padding: "4px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              overflowX: "auto",
            }}
          >
            {[
              { id: "All", label: "All Statuses", icon: null },
              { id: "Scheduled", label: "Scheduled", icon: <ScheduleIcon sx={{ fontSize: "0.95rem" }} /> },
              { id: "Completed", label: "Completed", icon: <CheckCircleIcon sx={{ fontSize: "0.95rem" }} /> },
              { id: "Blocked", label: "Blocked", icon: <BlockIcon sx={{ fontSize: "0.95rem" }} /> },
            ].map((tab) => {
              const isSelected = filterOptions.status === tab.id;
              return (
                <Button
                  key={tab.id}
                  size="small"
                  onClick={() => handleStatusChange({ target: { value: tab.id } })}
                  startIcon={tab.icon}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: isSelected ? 700 : 500,
                    borderRadius: "8px",
                    padding: "6px 14px",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease-in-out",
                    backgroundColor: isSelected ? "#ffffff" : "transparent",
                    color: isSelected ? colors.primary : "#64748b",
                    boxShadow: isSelected ? "0 2px 8px rgba(0, 0, 0, 0.06)" : "none",
                    border: isSelected ? `1px solid ${colors.primaryLightBorder}` : "1px solid transparent",
                    "&:hover": {
                      backgroundColor: isSelected ? "#ffffff" : "#f1f5f9",
                      color: isSelected ? colors.primary : "#1e293b",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            pt: 2,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", mr: 0.5 }}
            >
              Filters:
            </Typography>

            <FormControl size="small">
              <Select
                value={filterOptions.interviewType}
                onChange={handleInterviewTypeChange}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <SmartToyOutlinedIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "36px",
                  borderRadius: "10px",
                  fontSize: "0.825rem",
                  fontWeight: 500,
                  backgroundColor: filterOptions.interviewType !== "All" ? colors.primaryLight : "#ffffff",
                  borderColor: filterOptions.interviewType !== "All" ? colors.primaryLightBorder : "#cbd5e1",
                  color: filterOptions.interviewType !== "All" ? colors.primary : "#334155",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value="All">All Interview Types</MenuItem>
                <MenuItem value="AI Bot +">AI Bot +</MenuItem>
                <MenuItem value="Coding Test">Coding Assessment</MenuItem>
                <MenuItem value="AI Bot">AI Interview</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <Select
                value={dateFilter}
                onChange={handleDateFilterChange}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarTodayOutlinedIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "36px",
                  borderRadius: "10px",
                  fontSize: "0.825rem",
                  fontWeight: 500,
                  backgroundColor: dateFilter !== "All" ? colors.primaryLight : "#ffffff",
                  borderColor: dateFilter !== "All" ? colors.primaryLightBorder : "#cbd5e1",
                  color: dateFilter !== "All" ? colors.primary : "#334155",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value="All">All Dates</MenuItem>
                <MenuItem value="Yesterday">Yesterday</MenuItem>
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Tomorrow">Tomorrow</MenuItem>
                <MenuItem value="Custom">Custom Date...</MenuItem>
              </Select>
            </FormControl>

            {dateFilter === "Custom" && (
              <Box sx={{ width: 170 }}>
                <DatePicker
                  selected={customDate}
                  onChange={handleCustomDateChange}
                  placeholderText="Select date..."
                  dateFormat="yyyy-MM-dd"
                  isClearable
                  autoFocus
                  autoComplete="off"
                  className="custom-datepicker"
                />
              </Box>
            )}

            <FormControl size="small">
              <Select
                value={filterOptions.limit}
                onChange={handleRowsPerPageChange}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <TableRowsOutlinedIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "36px",
                  borderRadius: "10px",
                  fontSize: "0.825rem",
                  fontWeight: 500,
                  backgroundColor: "#ffffff",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={20}>20 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={handleResetFilters}
              startIcon={<RotateLeftIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: colors.primary,
                backgroundColor: colors.primaryLight,
                border: `1px solid ${colors.primaryLightBorder}`,
                borderRadius: "8px",
                px: 1.5,
                py: "5px",
                "&:hover": {
                  backgroundColor: colors.sidebarSelected,
                },
              }}
            >
              Clear {activeFilterCount} {activeFilterCount === 1 ? "Filter" : "Filters"}
            </Button>
          )}
        </Box>
      </Paper>
      <TableContainer className="custom-table-container">
        <Table>
          <TableHead>
            <TableRow>
              {headCells.map((headCell, index) => (
                <TableCell key={index}>{headCell.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {interview.isLoading ? (
              Array.from({ length: filterOptions.limit }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} />
              ))
            ) : interview.data?.data?.length > 0 ? (
              interview.data.data.map((data) => {
                const city = data?.candidateAddress?.city
                  ? data?.candidateAddress?.city + ","
                  : "";
                const state = data?.candidateAddress?.state
                  ? data?.candidateAddress?.state + ","
                  : "";
                const country = data?.candidateAddress?.country || "";
                const address = city + state + country;
                return (
                  <TableRow key={data?._id || data?.id}>
                    <TableCell>
                      <Box className="table_user-primary-detail">
                        <Avatar src={data?.profilePic} alt="profile" />
                        <Box>
                          <Typography>{data?.candidateName || ""}</Typography>
                          <Typography>{data?.candidateEmail || ""}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className="table_user-primary-detail">
                        <Box>
                          <Typography>{data?.recruiter || ""}</Typography>
                          <Typography>{data?.recruiterEmail || ""}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {String(data?.source ?? data?.sourcer ?? "").trim() ||
                          "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{data?.candidatePhone || ""}</TableCell>
                    <TableCell>{data?.applyFor || ""}</TableCell>
                    <TableCell>
                      {handleFormateDate(data?.startTime) || ""}
                    </TableCell>
                    <TableCell>
                      {handleFormateDate(data?.endTime) || ""}
                    </TableCell>
                    <TableCell>{address || ""}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          data?.interviewType === "Coding Test"
                            ? "Coding Assessment"
                            : data?.interviewType === "AI Bot" || !data?.interviewType
                              ? "AI Interview"
                              : data?.interviewType
                        }
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          backgroundColor:
                            data?.interviewType === "AI Bot +"
                              ? "#f3e5f5"
                              : data?.interviewType === "Coding Test"
                                ? "#e8f5e9"
                                : colors.primaryLight,
                          color:
                            data?.interviewType === "AI Bot +"
                              ? "#7b1fa2"
                              : data?.interviewType === "Coding Test"
                                ? "#2e7d32"
                                : colors.primary,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={data?.status || "Unknown"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "11.5px",
                          borderRadius: "6px",
                          backgroundColor:
                            data?.status === "Completed"
                              ? "#e8f5e9"
                              : data?.status === "Scheduled"
                                ? "#e3f2fd"
                                : data?.status === "Cancelled"
                                  ? "#f5f5f5"
                                  : data?.status === "Blocked"
                                    ? "#fff3e0"
                                    : "#f5f5f5",
                          color:
                            data?.status === "Completed"
                              ? "#2e7d32"
                              : data?.status === "Scheduled"
                                ? "#1565c0"
                                : data?.status === "Cancelled"
                                  ? "#616161"
                                  : data?.status === "Blocked"
                                    ? "#e65100"
                                    : "#616161",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        className="action-cell"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "0.4rem",
                        }}
                      >
                        <Tooltip
                          title="Edit"
                          placement="top"
                          slots={{
                            transition: Zoom,
                          }}
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: "preventOverflow",
                                options: {
                                  boundary: "window",
                                },
                              },
                            ],
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: colors.primary,
                                color: "#fff",
                                fontSize: "0.875rem",
                              },
                            },
                            arrow: {
                              sx: {
                                color: colors.primary,
                              },
                            },
                          }}
                        >
                          <IconButton
                            variant="outlined"
                            onClick={() => handleEditCandidate(data._id, data?.interviewType)}
                            size="small"
                            disabled={
                              interview.data?.data.length === 0 ||
                              data?.status === "Completed" ||
                              data?.status === "Cancelled"
                            }
                            sx={{
                              color: colors.primary,
                              borderColor: colors.primary,
                              ":hover": {
                                backgroundColor: colors.primary,
                                color: "#fff",
                                borderColor: colors.primary,
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: "large" }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title="Cancel"
                          placement="top"
                          slots={{
                            transition: Zoom,
                          }}
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: "preventOverflow",
                                options: {
                                  boundary: "window",
                                },
                              },
                            ],
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: colors.primary,
                                color: "#fff",
                                fontSize: "0.875rem",
                              },
                            },
                            arrow: {
                              sx: {
                                color: colors.primary,
                              },
                            },
                          }}
                        >
                          <IconButton
                            onClick={() =>
                              handleClickOpen(data._id, data?.candidateName, data?.interviewType)
                            }
                            size="small"
                            variant="outlined"
                            disabled={
                              data?.status === "Completed" ||
                              data?.status === "Cancelled"
                            }
                            sx={{
                              color: colors.primary,
                              borderColor: colors.primary,
                              ":hover": {
                                backgroundColor: colors.primary,
                                color: "#fff",
                                borderColor: colors.primary,
                              },
                              textTransform: "capitalize",
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={
                            data?.status !== "Completed"
                              ? "Report available after interview is completed"
                              : downloading[data?._id]
                              ? "Report generation is in progress. Please wait..."
                              : "Download Report"
                          }
                          placement="top"
                          slots={{
                            transition: Zoom,
                          }}
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: "preventOverflow",
                                options: {
                                  boundary: "window",
                                },
                              },
                            ],
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: colors.primary,
                                color: "#fff",
                                fontSize: "0.875rem",
                              },
                            },
                            arrow: {
                              sx: {
                                color: colors.primary,
                              },
                            },
                          }}
                        >
                          <span>
                            <IconButton
                              onClick={() => handleExportInterviewReport(data)}
                              size="small"
                              variant="outlined"
                              aria-label="Download interview report PDF"
                              disabled={
                                data?.status !== "Completed" ||
                                downloading[data?._id]
                              }
                              sx={{
                                color: downloading[data?._id]
                                  ? colors.primary
                                  : colors.primary,
                                borderColor: colors.primary,
                                ":hover": {
                                  backgroundColor: colors.primary,
                                  color: "#fff",
                                  borderColor: colors.primary,
                                },
                              }}
                            >
                              {downloading[data?._id] ? (
                                <CircularProgress size={20} sx={{ color: colors.primary }} />
                              ) : (
                                <FileDownloadOutlinedIcon sx={{ fontSize: "large" }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <EventNoteIcon sx={{ fontSize: 56, color: "#d1d5db" }} />
                    <Typography variant="h6" fontWeight={600} color="text.secondary">
                      No interviews found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your filters or schedule a new interview.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomConfirmModel
        title="Confirm Cancel"
        textContent="Are you sure you want to cancel the interview of"
        confirmButton="Confirm Cancel"
        open={open}
        handleClose={handleClose}
        name={candiName}
        handleAction={handleCancel}
        loadingButtonType="Cancelling"
        loading={cancelLoading}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        {interview.isLoading ? (
          <>
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="text" width={60} height={28} />
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "8px" }} />
          </>
        ) : (
          <>
            <Button
              className="btn"
              variant="contained"
              onClick={handlePrevPage}
              disabled={filterOptions.page === 1}
              startIcon={<NavigateBeforeIcon />}
              sx={{ borderRadius: "8px", textTransform: "capitalize", px: 2.5 }}
            >
              Previous
            </Button>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Page {filterOptions.page}
            </Typography>
            <Button
              className="btn"
              variant="contained"
              onClick={handleNextPage}
              disabled={interview.data?.data.length === 0}
              endIcon={<NavigateNextIcon />}
              sx={{ borderRadius: "8px", textTransform: "capitalize", px: 2.5 }}
            >
              Next
            </Button>
          </>
        )}
      </Box>
    </MainWrapper>
  );
};

export default ScheduledInterviewList;
